import type { IIntervals } from '@/components/agent/my-availability/types';

import { getOffsetData } from '@/helpers/contracts';
import { BLOCKS_IN_HOUR, BLOCK_DURATION, HOURS_IN_DAY } from '@/config/variables';

export const splitAvailabilityIntervals = (intervals: IIntervals[], timeZone: string): IIntervals[] => {
  return intervals.flatMap(interval => {
    const offset = getOffsetData(interval.startOffset, interval.endOffset, timeZone);

    const isShiftEndingAtMidnight = offset.endHour.hours === 0 && offset.endHour.minutes === 0;
    const isShiftEndingOnNextDay = offset.startHour.weekdayIndex !== offset.endHour.weekdayIndex;

    const shouldSplitInterval = isShiftEndingOnNextDay && !isShiftEndingAtMidnight;

    if (!shouldSplitInterval) return interval;

    const hoursUntilMidnight = HOURS_IN_DAY - offset.startHour.hours;
    const hoursAsBlocks = hoursUntilMidnight * BLOCKS_IN_HOUR;

    const minutes = offset.startHour.minutes;
    /**
     * e.g. if 22:30, then there are 24-22 = 2 hours = 4 blocks,
     * so we need to subtract the 30 minute block to make it 3 blocks
     */
    const minutesAsBlocks = minutes === BLOCK_DURATION ? -1 : 0;

    const blocksUntilMidnight = hoursAsBlocks + minutesAsBlocks;

    /**
     * offsets are inclusive:
     * e.g. {start: 1, end: 2}
     * means that the span is 2 blocks, starting from 1 and ending at the end of 2
     * so we should exclude the last block
     */
    const offsetToExcludeLastBlock = -1;

    const prevItem: IIntervals = {
      ...interval,
      startOffset: interval.startOffset,
      endOffset: interval.startOffset + blocksUntilMidnight + offsetToExcludeLastBlock,
    };
    const nextItem: IIntervals = {
      ...interval,
      startOffset: interval.startOffset + blocksUntilMidnight,
      endOffset: interval.endOffset,
    };

    return [prevItem, nextItem];
  });
};
